/**
 * useSubscription — Central source of truth for the user's billing state.
 *
 * Verkaufsstarker Trial-Modus:
 *   Statt User ohne Abo komplett zu sperren, dürfen sie 1 eigene Test-Akte
 *   komplett erstellen + bearbeiten. Erst beim Versuch der 2. Akte oder
 *   beim Klick auf bestimmte Funktionen wird ein Upsell-Dialog gezeigt.
 *
 * Felder:
 *   • status     → "active" | "inactive" | "cancelled" | "past_due" | "trialing"
 *   • isAdmin    → admin/backoffice (immer voll)
 *   • isActive   → echtes aktives Abo (Vollzugriff)
 *   • isTrial    → Gutachter ohne aktives Abo (Test-Modus, 1 Akte)
 *   • planId / plan
 *   • minEndAt / renewalAt / canCancel / cancelAvailableFrom
 *   • refresh()  → Re-Fetch
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// Behalten für rückwärtskompatible Imports — Demo-Akte gibt es nicht mehr.
export const DEMO_CASE_ID = "";

export function useSubscription() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSub = useCallback(async () => {
    if (!user) {
      setSub(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/billing/me/subscription");
      setSub(data);
    } catch {
      setSub({
        status: user.subscription_status || "inactive",
        plan_id: user.subscription_plan || "",
        is_admin: ["admin", "backoffice"].includes(user.role),
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchSub(); }, [fetchSub]);

  const isAdmin = !!sub?.is_admin || ["admin", "backoffice"].includes(user?.role);
  const status = sub?.status || "inactive";
  const isActive = status === "active";
  const isTrial = !isAdmin && !isActive;

  return {
    loading,
    locked: false,         // Trial-Modus statt Lockdown
    isAdmin,
    isActive,
    isTrial,
    status,
    planId: sub?.plan_id || "",
    plan: sub?.plan || null,
    startedAt: sub?.started_at || null,
    minEndAt: sub?.min_end_at || null,
    renewalAt: sub?.renewal_at || null,
    cancelAt: sub?.cancel_at || null,
    canCancel: !!sub?.can_cancel,
    cancelAvailableFrom: sub?.cancel_available_from || null,
    minTermMonths: sub?.min_term_months || 3,
    refresh: fetchSub,
  };
}
